import React from 'react';
import PropTypes from 'prop-types';
import { noop, debounce, ceil } from 'lodash';

// local dependencies
import { endScrollDetector as endScrollDetectorView } from './end-scroll-detector.view';
import { scrollListenerUtil } from 'utils/scrollListener';
import { isIosApp, isIosWeb } from 'core/modules/device-detect';

/**
 * @type {function}
 * @desc A Higher-Order-Component to enhance functionality of `Component`
 * @param {object} Component - A React component
 * @example
 * import { EndScrollDetector } from 'modules/end-scroll-detector';
 *
 *
 * <EndScrollDetector callback={} disabled={} offset={}>
 * 	 <div scrollable>...</div>
 * </EndScrollDetector>
 */
const endScrollDetector = ( Component = endScrollDetectorView ) => {
    class EndScrollDetector extends React.Component {
        constructor( props ) {
            log.debug( 'EndScrollDetector.constructor()' );

			super( props );

			// child view ref (scroll container)
			this.viewRef =  React.createRef();

			// bind events and methods
			this.__bind();
        }

        /**
		 * @desc Bind methods and events to the instance
		 */
		__bind() {
			log.debug( 'EndScrollDetector.__bind()' );

			this.hasBottomReached = this.hasBottomReached.bind( this );
			this.unbindScrollEvent = this.unbindScrollEvent.bind( this );
			this.bindScrollEvent = this.bindScrollEvent.bind( this );
			this.onScroll = debounce( this.onScroll, 200 ).bind( this );
		}

		/*******************************************/

		/**
		 * @desc Bind `scroll` events on `viewRef` element
		 */
		bindScrollEvent() {
			log.debug( 'EndScrollDetector.bindScrollEvent()' );

			if( this.props.isScrollOnChildren ) {

				scrollListenerUtil.listen( this.viewRef.current, this.onScroll );
			} else {

				scrollListenerUtil.listen( {}, this.onScroll );
			}

			// also check after mounting
			this.onScroll();

		}

		/**
		 * @desc Remove `scroll` events from `viewRef` element
		 */
		unbindScrollEvent() {
			log.debug( 'EndScrollDetector.unbindScrollEvent()' );

			if( this.props.isScrollOnChildren ) {

				scrollListenerUtil.unlisten( this.viewRef.current, this.onScroll );
			} else {

				scrollListenerUtil.unlisten( {}, this.onScroll );
			}

		}

		/**
		 * @desc Check if user has scrolled to the bottom of the container.
		 */
		hasBottomReached() {
			log.debug( 'EndScrollDetector.hasBottomReached()' );

			if( this.props.isScrollOnChildren ) {
				const { scrollTop, offsetHeight, scrollHeight } = this.viewRef.current;
				return 0 < scrollTop && ( ceil( scrollTop ) + offsetHeight ) >= scrollHeight - this.props.offset;
			} else {
				const scrollY = window.scrollY || window.pageYOffset;
				return  Math.ceil( window.innerHeight + scrollY ) >= document.documentElement.scrollHeight - this.props.offset;
			}
		}

		/**
		 * @desc When user scrolls, check if s/he reached the bottom of the scroll container.
		 * When at the bottom, call `props.callback` function
		 */
		onScroll() {
			log.debug( 'EndScrollDetector.onScroll()' );

			if( ! this.props.disabled && this.hasBottomReached() ) {
				this.props.callback();
			}
		}

		/****************************************************/

		/**
		 * @desc When component mounts, attach event listeners.
		 */
		componentDidMount() {
			log.debug( 'EndScrollDetector.componentDidMount()' );

			this.bindScrollEvent();
		}

		/**
		 * @desc When component unmounts, remove event listeners.
		 */
		componentWillUnmount() {
			log.debug( 'EndScrollDetector.componentWillUnmount()' );

			this.unbindScrollEvent();
		}

		/**
		 * @desc Render children.
		 */
		render() {
			log.debug( 'EndScrollDetector.render()' );

            return (
				<Component
					viewRef = { this.viewRef }
					render = { this.props.render }
					children = { this.props.children }
				/>
            );
		}
    }

    // set display name
    EndScrollDetector.displayName = 'EndScrollDetector';

    // default props
    EndScrollDetector.defaultProps = {
		disabled: false,
		offset: ( isIosApp() || isIosWeb() ) ? 150 : 0, // 100px offset for iOS devices
		callback: noop,
		isScrollOnChildren: true
	};

    // prop types
    EndScrollDetector.propTypes = {
		disabled: PropTypes.bool,
		offset: PropTypes.number,
		callback: PropTypes.func,
	};

    // return component
    return EndScrollDetector;
};

// export HOC
export { endScrollDetector };

// export react component with default view
export const EndScrollDetector = endScrollDetector();
